/* Two other ways to look at the same list.
 *
 * **Kanban** for anything with a status: an Issue list tells you there are
 * forty issues, a board tells you thirty-six are waiting on somebody.
 * **Calendar** for anything with a date: a Delivery Note list is rows, a
 * month is a plan.
 *
 * Both are the same rows the list already fetched, arranged differently —
 * not a second way of asking the server, which is how two views of one thing
 * start disagreeing.
 */
import { useEffect, useMemo, useState } from 'react'
import { formatCurrency, formatDate, parseFrappeDate, selectOptions } from './format'
import type { Doc, DocField, DocMeta } from './types'

export function KanbanView({ meta, rows, field, onOpen }: {
  meta: DocMeta; rows: Doc[]; field: DocField; onOpen: (name: string) => void
}) {
  const columns = useMemo(() => {
    const declared = selectOptions(field.options).map((o) => o.value).filter(Boolean)
    const seen = [...new Set(rows.map((r) => String(r[field.fieldname] ?? '')))]
    // Declared order first — a board whose columns reorder themselves as the
    // data changes is a board nobody can learn.
    return [...declared, ...seen.filter((s) => !declared.includes(s))]
  }, [field, rows])

  const money = meta.fields.find((f) => f.fieldtype === 'Currency' && f.in_list_view)

  return (
    <div style={{ display: 'grid', gap: 12, gridAutoFlow: 'column',
                  gridAutoColumns: 'minmax(230px, 1fr)', overflowX: 'auto', paddingBottom: 8 }}>
      {columns.map((column) => {
        const inColumn = rows.filter((r) => String(r[field.fieldname] ?? '') === column)
        return (
          <section key={column || '—'} className="card" style={{ padding: 8, minHeight: 120 }}>
            <div className="row-between" style={{ marginBottom: 8, padding: '0 4px' }}>
              <strong className="small">{column || 'Not set'}</strong>
              <span className="chip">{inColumn.length}</span>
            </div>
            <div className="stack" style={{ gap: 6 }}>
              {inColumn.map((row) => (
                <button key={String(row.name)} className="card card-pad" style={{ textAlign: 'left', padding: 10 }}
                        onClick={() => onOpen(String(row.name))}>
                  <div className="truncate" style={{ fontWeight: 550, fontSize: 13 }}>
                    {String((meta.title_field && row[meta.title_field]) || row.name)}
                  </div>
                  {money && row[money.fieldname] ? (
                    <div className="small muted" style={{ marginTop: 2 }}>
                      {formatCurrency(row[money.fieldname], String(row.currency ?? 'INR'))}
                    </div>
                  ) : null}
                </button>
              ))}
              {inColumn.length === 0 ? <div className="small muted" style={{ padding: 6 }}>Nothing here.</div> : null}
            </div>
          </section>
        )
      })}
    </div>
  )
}

export function CalendarView({ meta, rows, field, onOpen, onMove, canMove }: {
  meta: DocMeta; rows: Doc[]; field: DocField; onOpen: (name: string) => void
  /** Move an item to another day (YYYY-MM-DD) by dragging it there; reject to put it back.
   *  Without it the calendar is read-only (owner, 2026-10-09: drag and drop). */
  onMove?: (name: string, day: string) => Promise<unknown>
  canMove?: (name: string) => boolean
}) {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1) })
  /** Items shown on their new day while the move is saved, by name. */
  const [moved, setMoved] = useState<Record<string, string>>({})
  const [over, setOver] = useState<string | null>(null)
  useEffect(() => { setMoved({}) }, [rows])
  const iso = (y: number, m: number, d: number) =>
    `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
  async function drop(name: string, day: string) {
    if (!onMove) return
    setMoved((x) => ({ ...x, [name]: day }))
    try { await onMove(name, day) } catch { setMoved((x) => { const { [name]: _gone, ...rest } = x; return rest }) }
  }

  const byDay = useMemo(() => {
    const map = new Map<string, Doc[]>()
    for (const row of rows) {
      const date = parseFrappeDate(moved[String(row.name)] ?? row[field.fieldname])
      if (!date) continue
      const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
      map.set(key, [...(map.get(key) ?? []), row])
    }
    return map
  }, [rows, field, moved])

  const first = new Date(month.getFullYear(), month.getMonth(), 1)
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
  // Monday-first, which is how a working week is read here.
  const lead = (first.getDay() + 6) % 7
  const cells = [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)]

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="row" style={{ gap: 8 }}>
        <button className="btn btn-sm" aria-label="Previous month"
                onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>←</button>
        <strong className="small" style={{ minWidth: 150, textAlign: 'center' }}>
          {month.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
        </strong>
        <button className="btn btn-sm" aria-label="Next month"
                onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>→</button>
        <span className="grow" />
        <span className="small muted">by {field.label || field.fieldname}</span>
      </div>
      <div className="card" style={{ padding: 8 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))', gap: 4 }}>
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
            <div key={d} className="small muted" style={{ textAlign: 'center', padding: 4 }}>{d}</div>
          ))}
          {cells.map((day, i) => {
            if (day === null) return <div key={`b${i}`} />
            const key = `${month.getFullYear()}-${month.getMonth()}-${day}`
            const here = byDay.get(key) ?? []
            const dayIso = iso(month.getFullYear(), month.getMonth(), day)
            return (
              <div key={key}
                   onDragOver={onMove ? (e) => { e.preventDefault(); setOver(key) } : undefined}
                   onDragLeave={onMove ? () => setOver((o) => (o === key ? null : o)) : undefined}
                   onDrop={onMove ? (e) => {
                     e.preventDefault(); setOver(null)
                     const name = e.dataTransfer.getData('text/x-jd-name')
                     if (name) void drop(name, dayIso)
                   } : undefined}
                   aria-label={onMove ? `Drop here to move to ${dayIso}` : undefined}
                   style={{ minHeight: 78, border: `1px ${over === key ? 'dashed var(--accent)' : 'solid var(--line)'}`,
                            borderRadius: 'var(--r-sm)', padding: 4,
                            background: over === key ? 'var(--accent-soft, var(--paper))' : here.length ? 'var(--paper)' : 'transparent' }}>
                <div className="small muted" style={{ textAlign: 'right' }}>{day}</div>
                <div className="stack" style={{ gap: 2 }}>
                  {here.slice(0, 3).map((row) => (
                    <button key={String(row.name)} className="chip chip-info truncate"
                            style={{ maxWidth: '100%', cursor: onMove && (!canMove || canMove(String(row.name))) ? 'grab' : 'pointer' }}
                            title={String(row.name)}
                            draggable={Boolean(onMove) && (!canMove || canMove(String(row.name)))}
                            onDragStart={(e) => { e.dataTransfer.setData('text/x-jd-name', String(row.name)); e.dataTransfer.effectAllowed = 'move' }}
                            onClick={() => onOpen(String(row.name))}>
                      {String((meta.title_field && row[meta.title_field]) || row.name)}
                    </button>
                  ))}
                  {here.length > 3 ? <span className="small muted">+{here.length - 3} more</span> : null}
                </div>
              </div>
            )
          })}
        </div>
      </div>
      <p className="small muted" style={{ margin: 0 }}>
        Showing what is on this page of the list, by {(field.label || field.fieldname).toLowerCase()}
        {rows.length ? ` — ${formatDate(rows[0]?.[field.fieldname])} onwards` : ''}.
      </p>
    </div>
  )
}

/** The field a board or a calendar would key off, if the DocType has one. */
export function boardField(meta: DocMeta | null): DocField | null {
  if (!meta?.fields?.length) return null
  const status = meta.fields.find((f) => f.fieldtype === 'Select' && /status|state|stage/i.test(f.fieldname))
  return status ?? meta.fields.find((f) => f.fieldtype === 'Select' && f.in_list_view) ?? null
}

export function calendarField(meta: DocMeta | null): DocField | null {
  if (!meta?.fields?.length) return null
  const named = ['posting_date', 'transaction_date', 'date', 'schedule_date', 'due_date', 'expected_end_date']
  for (const n of named) {
    const f = meta.fields.find((x) => x.fieldname === n && (x.fieldtype === 'Date' || x.fieldtype === 'Datetime'))
    if (f) return f
  }
  return meta.fields.find((f) => f.fieldtype === 'Date' && f.in_list_view) ?? null
}
